// src/pages/AdminOnboard.jsx
// STATUS: active | UPDATED: 2026-09-01
// LOG:
//   2026-09-01  New. Bringing a dealership on is one job, so it is one screen.
//
// Signing a store used to mean: create the dealership on one page, add its
// locations on the same page but a different card, then go to a third page and
// invite people one at a time, choosing the dealership again for each of them.
// Four screens and a lot of re-deciding for what is a single act.
//
// This is that act. Find them, confirm who they are, add their buildings,
// invite their people, done.
//
// THE ROLES ARE SHAPED, NOT ASKED
//
// Every dealership has exactly one owner, at least one sales manager and a
// floor. So the people step starts with those rows already laid out and the
// roles already chosen, and the operator fills in names rather than answering
// "manager or employee" fifteen times. The shape of a dealership is knowledge
// this product already has, and asking for it every time is the software
// pretending not to know.
//
// NOTHING IS INVENTED
//
// The research fills in what it can actually see on their website and leaves
// the rest blank. A wizard that guesses a phone number to look helpful is the
// same fake data this repo bans everywhere else, and here it would be printed
// on the first screen a new dealership sees.
//
// EVERY STEP IS REVERSIBLE UNTIL THE INVITES GO
//
// Creating the business and its locations touches only our own tables. The
// invites send real email to real people, so they are last, they are explicit,
// and the summary shows exactly who is about to be contacted.

import { useState } from 'react'
import {
  Box, Flex, HStack, VStack, Text, Icon, Button, Input, Select,
  Alert, AlertIcon, useToast, SimpleGrid, Badge, IconButton,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { Search, Building2, MapPin, Users, Check, Plus, X, ArrowRight } from 'lucide-react'
import { supabase } from '../lib/supabase'

const STAGES = ['Find them', 'The business', 'Their locations', 'Their people']

function slugify(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60)
}

// Same rule as the Lookup card. Cut at the first real separator, then drop a
// trailing location clause, but never treat "of" as one: Mahindra of Oklahoma
// and Crain Hyundai of Fort Smith are the business name.
function businessNameFrom(f) {
  const raw = (f?.site_name || f?.title || '').trim()
  if (!raw) return ''
  let name = raw.split(/\s[|·–—]\s|:\s|\s-\s/)[0].trim()
  name = name.replace(/\s+(in|near|serving)\s+[A-Z][\w\s,.']*$/i, '').trim()
  return name.replace(/[,\s]+$/, '').slice(0, 60)
}

function Stage({ index, current, label }) {
  const done = index < current
  const active = index === current
  return (
    <HStack spacing={2} opacity={done || active ? 1 : 0.45}>
      <Flex
        boxSize="22px"
        borderRadius="full"
        bg={done ? 'ink' : active ? 'accent.500' : 'surface'}
        color={done || active ? 'onInk' : 'inkMuted'}
        align="center"
        justify="center"
        fontSize="11px"
        fontWeight={700}
        flexShrink={0}
      >
        {done ? <Check size={12} strokeWidth={3} /> : index + 1}
      </Flex>
      <Text fontSize="12px" fontWeight={active ? 600 : 500} color={active ? 'ink' : 'inkMuted'} whiteSpace="nowrap">
        {label}
      </Text>
    </HStack>
  )
}

export default function AdminOnboard() {
  const toast = useToast()
  const navigate = useNavigate()
  const [stage, setStage] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const [url, setUrl] = useState('')
  const [research, setResearch] = useState(null)
  const [biz, setBiz] = useState({ name: '', website: '', phone: '', makes: '', accent: '' })
  const [dealershipId, setDealershipId] = useState(null)
  const [logo, setLogo] = useState(null)
  const [locations, setLocations] = useState([{ name: '', city: '', state: '', website: '' }])

  // A dealership has one owner, a manager and a floor. Laid out that way from
  // the start rather than asked for one row at a time.
  const [people, setPeople] = useState([
    { name: '', email: '', role: 'owner' },
    { name: '', email: '', role: 'manager' },
    { name: '', email: '', role: 'employee' },
  ])
  const [invited, setInvited] = useState([])

  async function runResearch() {
    const target = url.trim()
    if (!target) return
    setBusy(true)
    setError(null)
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      setBusy(false)
      setError('Your session expired. Sign in again.')
      return
    }
    try {
      const res = await fetch('/.netlify/functions/research-dealership', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ url: target.startsWith('http') ? target : `https://${target}` }),
      })
      const body = await res.json()
      if (!res.ok) {
        setError(body.error || 'That did not work.')
      } else {
        const f = body.findings || {}
        setResearch(body)
        setBiz({
          name: businessNameFrom(f),
          website: body.source_url || target,
          phone: f.phone || '',
          makes: (f.makes || []).join(', '),
          accent: f.colors?.[0]?.hex || '',
        })
        setLocations([{ name: '', city: '', state: '', website: body.source_url || target }])
        setStage(1)
      }
    } catch {
      setError('Could not reach the research function.')
    }
    setBusy(false)
  }

  function skipResearch() {
    setResearch(null)
    setBiz({ name: '', website: url.trim(), phone: '', makes: '', accent: '' })
    setStage(1)
  }

  async function createBusiness() {
    const name = biz.name.trim()
    if (!name) {
      setError('Give the business a name.')
      return
    }
    setBusy(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('dealerships')
      .insert({
        name,
        slug: slugify(name),
        website: biz.website.trim() || null,
        phone: biz.phone.trim() || null,
        makes: biz.makes.trim() ? biz.makes.split(',').map((m) => m.trim()).filter(Boolean) : null,
        accent_hex: /^#[0-9A-Fa-f]{6}$/.test(biz.accent.trim()) ? biz.accent.trim() : null,
        subscription_status: 'trial',
      })
      .select()
      .single()
    setBusy(false)
    if (err || !data) {
      setError(err?.message || 'Could not create the business.')
      return
    }
    setDealershipId(data.id)
    if (research?.id) {
      await supabase.from('dealership_research').update({ dealership_id: data.id }).eq('id', research.id)
    }
    setLocations((rows) => rows.map((r) => ({ ...r, name: r.name || name })))

    // Their own logo, off their own site, without asking anybody to go and find
    // a file. Best candidate only; the rest are offered on the dealership page
    // if this one is wrong.
    const candidate = research?.findings?.logo_candidates?.[0]
    if (candidate) {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const res = await fetch('/.netlify/functions/fetch-logo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ url: candidate.url, dealershipId: data.id }),
        })
        const out = await res.json()
        if (out.ok) setLogo({ url: out.url, source: candidate.source })
      } catch {
        // A missing logo is not a reason to stop onboarding a dealership.
      }
    }

    setStage(2)
  }

  async function saveLocations() {
    const rows = locations.filter((l) => l.name.trim())
    if (rows.length === 0) {
      setError('A dealership needs at least one location.')
      return
    }
    setBusy(true)
    setError(null)
    const { error: err } = await supabase.from('locations').insert(
      rows.map((l, i) => ({
        dealership_id: dealershipId,
        name: l.name.trim(),
        slug: slugify(l.name),
        city: l.city.trim() || null,
        state: l.state.trim() || null,
        website: l.website.trim() || null,
        is_primary: i === 0,
      }))
    )
    setBusy(false)
    if (err) {
      setError(err.message)
      return
    }
    setStage(3)
  }

  async function sendInvites() {
    const rows = people.filter((p) => p.email.trim() && p.name.trim())
    if (rows.length === 0) {
      setError('Add at least one person, or finish without inviting anybody.')
      return
    }
    setBusy(true)
    setError(null)
    const { data: { session } } = await supabase.auth.getSession()
    const results = []
    // One at a time and reported individually. A batch that half succeeds and
    // reports success is how somebody gets left out of their own store.
    for (const p of rows) {
      try {
        const res = await fetch('/.netlify/functions/invite-user', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({
            email: p.email.trim(),
            displayName: p.name.trim(),
            role: p.role,
            dealershipId,
          }),
        })
        const body = await res.json()
        results.push({ ...p, ok: res.ok && !body.error, error: body.error || null })
      } catch {
        results.push({ ...p, ok: false, error: 'Could not reach the invite function.' })
      }
    }
    setBusy(false)
    setInvited(results)
    const failed = results.filter((r) => !r.ok)
    toast({
      status: failed.length === 0 ? 'success' : 'warning',
      title:
        failed.length === 0
          ? `${results.length} ${results.length === 1 ? 'invite' : 'invites'} sent`
          : `${results.length - failed.length} sent, ${failed.length} did not`,
    })
  }

  return (
    <VStack align="stretch" spacing={6} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          Operator
        </Text>
        <Text textStyle="pageTitle">Bring a dealership on</Text>
        <Text fontSize="body" color="inkMuted" lineHeight={1.65} maxW="measure" mt={3}>
          Find them, confirm who they are, add their buildings, invite their people. Nothing is
          emailed until the last step.
        </Text>
      </Box>

      <HStack spacing={5} wrap="wrap" pb={2} borderBottom="1px solid" borderColor="line">
        {STAGES.map((label, i) => <Stage key={label} index={i} current={stage} label={label} />)}
      </HStack>

      {error && (
        <Alert status="error" borderRadius="md" fontSize="sm">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* 1. FIND THEM ------------------------------------------------------ */}
      {stage === 0 && (
        <Box bg="card" border="1px solid" borderColor="line" borderRadius="card" p={{ base: 5, md: 6 }}>
          <HStack spacing={2} mb={2}>
            <Icon as={Search} boxSize={4} color="accent.500" />
            <Text textStyle="eyebrow" color="accent.500">
              Their website
            </Text>
          </HStack>
          <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6} maxW="measure" mb={4}>
            We read their public homepage and fill in what is actually there. Nothing is guessed,
            and you can correct all of it on the next screen.
          </Text>
          <Flex gap={3} wrap="wrap">
            <Input
              flex="1 1 280px"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runResearch()}
              placeholder="example-motors.com"
              bg="surface"
              borderColor="line"
              fontSize="body-sm"
              _focusVisible={{ borderColor: 'accent.500', boxShadow: 'none' }}
            />
            <Button bg="ink" color="onInk" _hover={{ opacity: 0.88 }} onClick={runResearch} isLoading={busy} loadingText="Reading">
              Look them up
            </Button>
          </Flex>
          <Button variant="ghost" size="sm" color="inkMuted" mt={3} onClick={skipResearch}>
            They have no usable website, add them by hand
          </Button>
        </Box>
      )}

      {/* 2. THE BUSINESS --------------------------------------------------- */}
      {stage === 1 && (
        <Box bg="card" border="1px solid" borderColor="line" borderRadius="card" p={{ base: 5, md: 6 }}>
          <HStack spacing={2} mb={4}>
            <Icon as={Building2} boxSize={4} color="accent.500" />
            <Text textStyle="eyebrow" color="accent.500">
              {research ? 'What we found' : 'The business'}
            </Text>
          </HStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={4}>
            {[
              ['name', 'Business name'],
              ['website', 'Website'],
              ['phone', 'Phone'],
              ['makes', 'Makes, comma separated'],
            ].map(([k, label]) => (
              <Box key={k}>
                <Text textStyle="eyebrow" color="inkMuted" mb={1}>
                  {label}
                </Text>
                <Input
                  value={biz[k]}
                  onChange={(e) => setBiz((b) => ({ ...b, [k]: e.target.value }))}
                  bg="surface"
                  borderColor="line"
                  size="sm"
                  fontSize="body-sm"
                  _focusVisible={{ borderColor: 'accent.500', boxShadow: 'none' }}
                />
              </Box>
            ))}
          </SimpleGrid>

          {research?.summary && (
            <Box bg="surface" borderRadius="md" p={4} mb={4}>
              <Text fontSize="body-sm" color="inkMuted" lineHeight={1.65} maxW="measure">
                {research.summary}
              </Text>
            </Box>
          )}

          <HStack spacing={2}>
            <Button bg="ink" color="onInk" _hover={{ opacity: 0.88 }} onClick={createBusiness} isLoading={busy} rightIcon={<ArrowRight size={15} />}>
              Create this business
            </Button>
            <Button variant="ghost" color="inkMuted" onClick={() => setStage(0)}>
              Back
            </Button>
          </HStack>
        </Box>
      )}

      {/* 3. LOCATIONS ------------------------------------------------------ */}
      {stage === 2 && (
        <Box bg="card" border="1px solid" borderColor="line" borderRadius="card" p={{ base: 5, md: 6 }}>
          <HStack spacing={2} mb={2}>
            <Icon as={MapPin} boxSize={4} color="accent.500" />
            <Text textStyle="eyebrow" color="accent.500">
              Their buildings
            </Text>
          </HStack>
          <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6} maxW="measure" mb={4}>
            Every dealership has at least one. A storefront report is run against a location, so
            give each its own page address where they have one.
          </Text>

          {logo && (
            <HStack bg="surface" borderRadius="md" px={4} py={3} spacing={4} mb={4} align="center">
              <Box as="img" src={logo.url} alt="" maxH="34px" maxW="140px" objectFit="contain" />
              <Box>
                <Text fontSize="body-sm" color="ink" fontWeight={600}>
                  Got their logo
                </Text>
                <Text fontSize="12px" color="inkMuted">
                  Pulled from their site, found as the {logo.source}. Replace it on the dealership
                  page if it is the wrong one.
                </Text>
              </Box>
            </HStack>
          )}

          <VStack align="stretch" spacing={2} mb={4}>
            {locations.map((l, i) => (
              <HStack key={i} spacing={2} align="center">
                <Input
                  flex="1 1 140px" size="sm" placeholder="Location name" value={l.name}
                  onChange={(e) => setLocations((r) => r.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                  bg="surface" borderColor="line" fontSize="body-sm"
                />
                <Input
                  flex="1 1 110px" size="sm" placeholder="City" value={l.city}
                  onChange={(e) => setLocations((r) => r.map((x, j) => (j === i ? { ...x, city: e.target.value } : x)))}
                  bg="surface" borderColor="line" fontSize="body-sm"
                />
                <Input
                  w="64px" size="sm" placeholder="ST" value={l.state}
                  onChange={(e) => setLocations((r) => r.map((x, j) => (j === i ? { ...x, state: e.target.value } : x)))}
                  bg="surface" borderColor="line" fontSize="body-sm"
                />
                {locations.length > 1 && (
                  <IconButton
                    aria-label="Remove location" size="sm" variant="ghost" color="inkMuted"
                    icon={<X size={14} />}
                    onClick={() => setLocations((r) => r.filter((_, j) => j !== i))}
                  />
                )}
              </HStack>
            ))}
          </VStack>

          <Button
            size="sm" variant="ghost" color="ink" leftIcon={<Plus size={14} />} mb={4}
            onClick={() => setLocations((r) => [...r, { name: '', city: '', state: '', website: '' }])}
          >
            Another location
          </Button>

          <HStack spacing={2}>
            <Button bg="ink" color="onInk" _hover={{ opacity: 0.88 }} onClick={saveLocations} isLoading={busy} rightIcon={<ArrowRight size={15} />}>
              Save and invite their people
            </Button>
          </HStack>
        </Box>
      )}

      {/* 4. PEOPLE --------------------------------------------------------- */}
      {stage === 3 && invited.length === 0 && (
        <Box bg="card" border="1px solid" borderColor="line" borderRadius="card" p={{ base: 5, md: 6 }}>
          <HStack spacing={2} mb={2}>
            <Icon as={Users} boxSize={4} color="accent.500" />
            <Text textStyle="eyebrow" color="accent.500">
              Their people
            </Text>
          </HStack>
          <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6} maxW="measure" mb={4}>
            The owner, whoever runs the floor, and the salespeople. First names only, because that
            is how the app greets them and how every email addresses them. Leave a row blank to
            skip it.
          </Text>

          <VStack align="stretch" spacing={2} mb={4}>
            {people.map((p, i) => (
              <HStack key={i} spacing={2}>
                <Input
                  flex="1 1 120px" size="sm" placeholder="First name" value={p.name}
                  onChange={(e) => setPeople((r) => r.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                  bg="surface" borderColor="line" fontSize="body-sm"
                />
                <Input
                  flex="2 1 200px" size="sm" placeholder="Email" type="email" value={p.email}
                  onChange={(e) => setPeople((r) => r.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
                  bg="surface" borderColor="line" fontSize="body-sm"
                />
                <Select
                  w="130px" size="sm" value={p.role} bg="surface" borderColor="line" fontSize="body-sm"
                  onChange={(e) => setPeople((r) => r.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)))}
                >
                  <option value="owner">Owner</option>
                  <option value="manager">Sales manager</option>
                  <option value="employee">Salesperson</option>
                </Select>
                {people.length > 1 && (
                  <IconButton
                    aria-label="Remove person" size="sm" variant="ghost" color="inkMuted"
                    icon={<X size={14} />}
                    onClick={() => setPeople((r) => r.filter((_, j) => j !== i))}
                  />
                )}
              </HStack>
            ))}
          </VStack>

          <Button
            size="sm" variant="ghost" color="ink" leftIcon={<Plus size={14} />} mb={4}
            onClick={() => setPeople((r) => [...r, { name: '', email: '', role: 'employee' }])}
          >
            Another salesperson
          </Button>

          <HStack spacing={2} wrap="wrap">
            <Button bg="ink" color="onInk" _hover={{ opacity: 0.88 }} onClick={sendInvites} isLoading={busy} loadingText="Sending">
              Send the invites
            </Button>
            <Button variant="ghost" color="inkMuted" onClick={() => navigate('/admin/dealerships/')}>
              Finish without inviting anybody
            </Button>
          </HStack>
        </Box>
      )}

      {/* DONE -------------------------------------------------------------- */}
      {invited.length > 0 && (
        <Box bg="ink" color="onInk" borderRadius="cardLg" p={{ base: 6, md: 8 }}>
          <HStack spacing={3} mb={4}>
            <Flex boxSize="30px" borderRadius="full" bg="accent.500" align="center" justify="center">
              <Check size={15} strokeWidth={3} />
            </Flex>
            <Text textStyle="eyebrow" color="accent.500">
              {biz.name} is on
            </Text>
          </HStack>

          <VStack align="stretch" spacing={2} mb={6}>
            {invited.map((p) => (
              <HStack key={p.email} spacing={3}>
                <Badge
                  bg={p.ok ? 'onInkFill' : 'accent.500'} color="onInk" fontSize="10px"
                  textTransform="uppercase" letterSpacing="0.07em" px={2} py={0.5} borderRadius="full"
                >
                  {p.ok ? 'invited' : 'failed'}
                </Badge>
                <Text fontSize="body-sm" fontWeight={600}>
                  {p.name}
                </Text>
                <Text fontSize="body-sm" color="onInkMuted" noOfLines={1}>
                  {p.error || p.email}
                </Text>
              </HStack>
            ))}
          </VStack>

          <HStack spacing={2} wrap="wrap">
            <Button bg="onInk" color="ink" _hover={{ bg: 'onInkStrong' }} onClick={() => navigate('/admin/dealerships/')}>
              Run their storefront report
            </Button>
            <Button variant="ghost" color="onInkSubtle" _hover={{ bg: 'onInkFill', color: 'onInk' }} onClick={() => window.location.reload()}>
              Bring on another
            </Button>
          </HStack>
        </Box>
      )}
    </VStack>
  )
}
